// AHA-47094 — decides whether the one-time "Convert to lesson" discovery nudge
// should appear in the presentation editor.
//
// The menu entry is a *seek* touchpoint: cheap, low-clutter, and therefore
// undiscoverable on its own. The nudge is the one concession to that — it must
// show at most once per user, be dismissible, and never appear for someone who
// already found the action on their own.
//
// Storage shape follows the marketplace coachmark idiom in storageKeys.js: a
// JSON array of user ids under one key, so a shared browser does not leak one
// user's "seen" state onto another. Every read/write is guarded — localStorage
// throws outright in some privacy modes, and a nudge must never break the
// editor.
import storageKeys from '@/constant/storageKeys'

const { CONVERT_TO_LESSON_NUDGE_SEEN, CONVERT_TO_LESSON_USED } = storageKeys

// The create-first-slide onboarding cohorts land in the editor with the
// slide-type picker already open, which this nudge visually overlaps.
export const CREATE_FIRST_SLIDE_DEEP_LINK_SOURCE = 'onboarding-create-first-slide'

const readUserIds = (key) => {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

const addUserId = (key, userId) => {
  if (userId === null || userId === undefined || userId === '') return
  const id = String(userId)
  const ids = readUserIds(key)
  if (ids.includes(id)) return
  try {
    window.localStorage.setItem(key, JSON.stringify([...ids, id]))
  } catch (e) {
    // Storage unavailable (private mode, quota). The nudge simply reappears
    // next session — acceptable; breaking the editor is not.
  }
}

const hasUserId = (key, userId) => {
  if (userId === null || userId === undefined || userId === '') return false
  return readUserIds(key).includes(String(userId))
}

/** The user has seen (and dismissed, or navigated past) the nudge before. */
export const hasSeenConvertToLessonNudge = (userId) => hasUserId(CONVERT_TO_LESSON_NUDGE_SEEN, userId)

/** The user already found the action on their own — never nudge them about it. */
export const hasUsedConvertToLesson = (userId) => hasUserId(CONVERT_TO_LESSON_USED, userId)

export const markConvertToLessonNudgeSeen = (userId) => addUserId(CONVERT_TO_LESSON_NUDGE_SEEN, userId)

export const markConvertToLessonUsed = (userId) => addUserId(CONVERT_TO_LESSON_USED, userId)

/** True when the editor was reached via a create-first-slide onboarding cohort. */
export const isCreateFirstSlideCohortOrigin = (deepLinkSource) => (
  deepLinkSource === CREATE_FIRST_SLIDE_DEEP_LINK_SOURCE
)

const CAPTURED_DEEP_LINK_SOURCE_KEY = 'convertToLessonNudge:capturedDeepLinkSource'

/**
 * Captures `deepLinkSource` from a router.beforeEach guard (router.js), which
 * runs before any component on the destination route mounts — unlike reading
 * `$route.query` from inside the editor, this does not depend on
 * PresentationNameMenu's own v-if/watcher timing relative to
 * PresentationEditor's history.replaceState strip. Overwrites (or clears) on
 * every navigation into the editor route, so a stale value from an earlier
 * visit never leaks into a later one.
 */
export const captureDeepLinkSource = (deepLinkSource) => {
  try {
    if (deepLinkSource) {
      sessionStorage.setItem(CAPTURED_DEEP_LINK_SOURCE_KEY, deepLinkSource)
    } else {
      sessionStorage.removeItem(CAPTURED_DEEP_LINK_SOURCE_KEY)
    }
  } catch (e) {
    // sessionStorage unavailable (private mode, cross-origin iframe) — best-effort.
  }
}

const BELOW_HOSTING_THRESHOLD_KEY = 'convertToLessonNudge:belowHostingThreshold'

/** Remembers for this browser session that the user's hosted-session count was checked and fell short. */
export const markBelowHostingThresholdThisSession = (userId) => {
  if (userId === null || userId === undefined || userId === '') return
  try {
    sessionStorage.setItem(BELOW_HOSTING_THRESHOLD_KEY, String(userId))
  } catch (e) {
    // sessionStorage unavailable — the count is simply asked again next editor load.
  }
}

export const wasBelowHostingThresholdThisSession = (userId) => {
  if (userId === null || userId === undefined || userId === '') return false
  try {
    return sessionStorage.getItem(BELOW_HOSTING_THRESHOLD_KEY) === String(userId)
  } catch (e) {
    return false
  }
}

/** Reads back the value written by captureDeepLinkSource(). */
export const getCapturedDeepLinkSource = () => {
  try {
    return sessionStorage.getItem(CAPTURED_DEEP_LINK_SOURCE_KEY)
  } catch (e) {
    return null
  }
}

/**
 * The single decision point for showing the nudge.
 *
 * `enabled` carries the caller's own preconditions (flag on, editor route,
 * user can manage the deck) so this stays a pure predicate over storage.
 *
 * `deepLinkSource` suppresses the nudge for the create-first-slide onboarding
 * cohorts, whose first editor load already opens the slide-type picker the
 * nudge would overlap. Every other caller is unaffected.
 */
export const shouldShowConvertToLessonNudge = ({ userId, enabled, deepLinkSource } = {}) => {
  if (!enabled) return false
  if (userId === null || userId === undefined || userId === '') return false
  if (isCreateFirstSlideCohortOrigin(deepLinkSource)) return false
  if (hasUsedConvertToLesson(userId)) return false
  return !hasSeenConvertToLessonNudge(userId)
}

export default {
  hasSeenConvertToLessonNudge,
  hasUsedConvertToLesson,
  markConvertToLessonNudgeSeen,
  markConvertToLessonUsed,
  isCreateFirstSlideCohortOrigin,
  captureDeepLinkSource,
  getCapturedDeepLinkSource,
  markBelowHostingThresholdThisSession,
  wasBelowHostingThresholdThisSession,
  shouldShowConvertToLessonNudge,
}
